import { makeStyles, mergeClasses } from '@fluentui/react-components';
import React from 'react';
import { Field } from 'react-final-form';
import { useStackWithFullWidthItemStyles } from '../../../themes/styles/flexbox-styles';
import { ProjectViewModel } from '../../common/models';
import { LegacyAddOrUpdateExpirationFormFieldGroup } from '../../environment/expiration-date/legacy-add-or-update-expiration-form-field-group/legacy-add-or-update-expiration-form-field-group';
import { AddEnvironmentFormFieldName, EnvironmentDefinitionViewModel, EnvironmentTypeViewModel } from '../models';
import AddEnvironmentFormEnvironmentDefinitionControl from './add-environment-form-environment-definition-control';
import AddEnvironmentFormEnvironmentTypeControl from './add-environment-form-environment-type-control';
import AddEnvironmentFormNameControl from './add-environment-form-name-control';
import AddEnvironmentFormProjectControl from './add-environment-form-project-control';

interface AddEnvironmentFormFieldGroupProps {
    isSubmitting: boolean;
    projects: ProjectViewModel[];
    environmentTypes: EnvironmentTypeViewModel[];
    environmentDefinitions: EnvironmentDefinitionViewModel[];
    environmentDefinitionOnChange: (value: EnvironmentDefinitionViewModel | undefined) => void;
    selectedProject: ProjectViewModel | undefined;
    selectedEnvironmentDefinition: EnvironmentDefinitionViewModel | undefined;
    selectedEnvironmentType: EnvironmentTypeViewModel | undefined;
    didSomeProjectFailToLoadCreateResources: boolean;
    enableExpiration: boolean;
}

/**
 * Styles
 */

const useContainerStyles = makeStyles({
    root: {
        gap: '16px',
    },
});

/**
 * End Styles
 */

export const AddEnvironmentFormFieldGroup: React.FC<AddEnvironmentFormFieldGroupProps> = (props) => {
    const {
        isSubmitting,
        projects,
        environmentTypes,
        environmentDefinitions,
        environmentDefinitionOnChange,
        selectedEnvironmentDefinition,
        selectedEnvironmentType,
        selectedProject,
        didSomeProjectFailToLoadCreateResources,
        enableExpiration,
    } = props;

    // Style hooks
    const containerStyles = useContainerStyles();
    const stackStyles = useStackWithFullWidthItemStyles();

    return (
        <div className={mergeClasses(stackStyles.root, containerStyles.root)}>
            <div className={stackStyles.item}>
                <Field<string> name={AddEnvironmentFormFieldName.EnvironmentName}>
                    {(fieldProps) => {
                        const { input, meta } = fieldProps;
                        const { value, onChange } = input;
                        const { error, pristine } = meta;

                        const errorMessage = !pristine ? error : undefined;

                        return (
                            <AddEnvironmentFormNameControl
                                environmentName={value}
                                onChange={onChange}
                                isSubmitting={isSubmitting}
                                errorMessage={errorMessage}
                            />
                        );
                    }}
                </Field>
            </div>
            <div className={stackStyles.item}>
                <Field<ProjectViewModel> name={AddEnvironmentFormFieldName.SelectedProject}>
                    {(fieldProps) => {
                        const { input, meta } = fieldProps;
                        const { onChange } = input;
                        const { error, pristine } = meta;

                        const errorMessage = !pristine ? error : undefined;

                        return (
                            <AddEnvironmentFormProjectControl
                                projects={projects}
                                onChange={onChange}
                                isSubmitting={isSubmitting}
                                selectedProject={selectedProject}
                                errorMessage={errorMessage}
                                showFailedToLoadWarning={didSomeProjectFailToLoadCreateResources}
                            />
                        );
                    }}
                </Field>
            </div>
            {!!selectedProject && (
                <div className={stackStyles.item}>
                    <Field<EnvironmentTypeViewModel> name={AddEnvironmentFormFieldName.SelectedEnvironmentType}>
                        {(fieldProps) => {
                            const { input, meta } = fieldProps;
                            const { onChange } = input;
                            const { error, pristine } = meta;

                            const errorMessage = !pristine ? error : undefined;

                            return (
                                <AddEnvironmentFormEnvironmentTypeControl
                                    environmentTypes={environmentTypes}
                                    onChange={onChange}
                                    isSubmitting={isSubmitting}
                                    selectedEnvironmentType={selectedEnvironmentType}
                                    errorMessage={errorMessage}
                                />
                            );
                        }}
                    </Field>
                </div>
            )}
            {!!selectedProject && (
                <div className={stackStyles.item}>
                    <Field<EnvironmentDefinitionViewModel>
                        name={AddEnvironmentFormFieldName.SelectedEnvironmentDefinition}
                    >
                        {(fieldProps) => {
                            const { meta } = fieldProps;
                            const { error, pristine } = meta;

                            const errorMessage = !pristine ? error : undefined;

                            return (
                                <AddEnvironmentFormEnvironmentDefinitionControl
                                    environmentDefinitions={environmentDefinitions}
                                    onChange={environmentDefinitionOnChange}
                                    isSubmitting={isSubmitting}
                                    selectedEnvironmentDefinition={selectedEnvironmentDefinition}
                                    errorMessage={errorMessage}
                                />
                            );
                        }}
                    </Field>
                </div>
            )}
            {!!selectedProject && (
                <div className={stackStyles.item}>
                    <LegacyAddOrUpdateExpirationFormFieldGroup // when this panel gets migrated to Fluent 9, the whole legacy-add-or-update-expiration-form-field-group folder can be deleted.
                        enableExpiration={enableExpiration}
                        isSubmitting={isSubmitting}
                    />
                </div>
            )}
        </div>
    );
};

export default AddEnvironmentFormFieldGroup;
